// ─── BezierPenLayer – Konva Rendering Component ───────────────────────────────
// Renders all bezierPen shapes using an HTML5 Canvas <Shape> node that calls
// ctx.bezierCurveTo() for real cubic Bézier curves — no tension trick.
// Also renders the anchor / handle editing UI when a bezierPen shape is selected.

import React, { useCallback } from 'react';
import { Layer, Shape as KonvaShape, Circle, Line, Group, Text as KonvaText, Rect } from 'react-konva';
import type Konva from 'konva';
import { Context } from 'konva/lib/Context';
// ↑ Konva's Context wrapper — sceneFunc/hitFunc receive this, NOT CanvasRenderingContext2D.
//   Unwrap the real canvas context via konvaCtx._context.
import type { BezierPenShape, BezierAnchor } from '../types/beizure.types';
import { mirrorHandle, bezierBoundingBox } from '../utils/beizerUtlis';

// ── Visual constants ──────────────────────────────────────────────────────────
const ANCHOR_RADIUS = 5;
const HANDLE_RADIUS = 4;
const ANCHOR_FILL   = '#ffffff';
const ANCHOR_STROKE = '#1565c0';
const HANDLE_FILL   = '#ffcc00';
const HANDLE_STROKE = '#333333';
const GUIDE_STROKE  = '#888888';
const HIT_THRESHOLD = 8; // px in canvas space for click detection

interface BezierPenLayerProps {
  shapes: BezierPenShape[];
  selectedId: string | null;
  stageScale: number;
  tool: string;
  onSelect: (id: string) => void;
  onShapeUpdate: (shape: BezierPenShape) => void;
  onShapeDragEnd: (shape: BezierPenShape) => void;
  formatMeasurement: (px: number) => string;
}

// ── Draw cubic Bézier path on a raw CanvasRenderingContext2D ──────────────────
// Accepts the unwrapped context so it stays usable outside Konva callbacks too.
function drawCubicPath(
  ctx: CanvasRenderingContext2D,
  anchors: BezierAnchor[],
  closed: boolean
) {
  if (anchors.length < 2) {
    if (anchors.length === 1) {
      ctx.beginPath();
      ctx.arc(anchors[0].x, anchors[0].y, 2, 0, Math.PI * 2);
      ctx.fill();
    }
    return;
  }

  ctx.beginPath();
  ctx.moveTo(anchors[0].x, anchors[0].y);

  for (let i = 1; i < anchors.length; i++) {
    const prev = anchors[i - 1];
    const curr = anchors[i];
    ctx.bezierCurveTo(
      prev.outHandle.x, prev.outHandle.y,
      curr.inHandle.x,  curr.inHandle.y,
      curr.x,           curr.y
    );
  }

  if (closed && anchors.length >= 2) {
    const last  = anchors[anchors.length - 1];
    const first = anchors[0];
    ctx.bezierCurveTo(
      last.outHandle.x,  last.outHandle.y,
      first.inHandle.x,  first.inHandle.y,
      first.x,           first.y
    );
    ctx.closePath();
  }
}

// ── Individual shape renderer ─────────────────────────────────────────────────
interface BezierShapeNodeProps {
  shape: BezierPenShape;
  isSelected: boolean;
  isHovered: boolean;
  stageScale: number;
  tool: string;
  onSelect: (id: string) => void;
  onShapeUpdate: (shape: BezierPenShape) => void;
  onShapeDragEnd: (shape: BezierPenShape) => void;
  formatMeasurement: (px: number) => string;
}

const BezierShapeNode: React.FC<BezierShapeNodeProps> = ({
  shape,
  isSelected,
  isHovered,
  stageScale,
  tool,
  onSelect,
  onShapeUpdate,
  onShapeDragEnd,
  formatMeasurement,
}) => {
  const r         = Math.max(ANCHOR_RADIUS, ANCHOR_RADIUS / stageScale);
  const hr        = Math.max(HANDLE_RADIUS, HANDLE_RADIUS / stageScale);
  const hitThresh = HIT_THRESHOLD / stageScale;

  // ── sceneFunc ──────────────────────────────────────────────────────────────
  // Konva passes its own Context wrapper as the first argument, not a raw
  // CanvasRenderingContext2D. We unwrap via ._context to get bezierCurveTo etc.
  const sceneFunc = useCallback(
    (konvaCtx: Context, konvaShape: Konva.Shape) => {
      const ctx = konvaCtx._context; // ← raw CanvasRenderingContext2D

      ctx.strokeStyle = shape.stroke || '#000000';
      ctx.lineWidth   = shape.strokeWidth || 2;
      ctx.fillStyle   = shape.fill && shape.fill !== 'transparent'
        ? shape.fill
        : 'rgba(0,0,0,0)';
      ctx.lineCap  = 'round';
      ctx.lineJoin = 'round';

      drawCubicPath(ctx, shape.anchors, shape.closed);

      if (shape.fill && shape.fill !== 'transparent') ctx.fill();
      ctx.stroke();

      konvaShape.getLayer()?.batchDraw();
    },
    [shape]
  );

  // ── hitFunc ────────────────────────────────────────────────────────────────
  // Same pattern: Konva Context wrapper → unwrap → raw canvas ops.
  const hitFunc = useCallback(
    (konvaCtx: Context, _konvaShape: Konva.Shape) => {
      const ctx = konvaCtx._context; // ← raw CanvasRenderingContext2D

      ctx.strokeStyle = 'black';
      ctx.lineWidth   = (shape.strokeWidth || 2) + hitThresh * 2;
      ctx.lineCap     = 'round';

      drawCubicPath(ctx, shape.anchors, shape.closed);
      ctx.stroke();
    },
    [shape, hitThresh]
  );

  const bounds = bezierBoundingBox(shape.anchors);

  // ── Shared coordinate helper ──────────────────────────────────────────────
  // For draggable Circle nodes we use e.target.x() / e.target.y() directly.
  // These are already in the parent Group's local coordinate space (= canvas
  // space, because the Group sits at 0,0 and is only translated during a
  // whole-shape drag which we reset on dragEnd).
  // Using pointer position + stage transform inversion causes a double-offset
  // when zoom/pan is active and is the root cause of the "jumping" bug.
  const nodePos = (e: Konva.KonvaEventObject<DragEvent>) => ({
    x: e.target.x(),
    y: e.target.y(),
  });

  // ── Anchor drag ────────────────────────────────────────────────────────────
  const handleAnchorDrag = (
    e: Konva.KonvaEventObject<DragEvent>,
    anchorIdx: number
  ) => {
    const pos     = nodePos(e);
    const anchors = shape.anchors.slice();
    const anchor  = { ...anchors[anchorIdx] };
    const dx = pos.x - anchor.x;
    const dy = pos.y - anchor.y;

    anchor.x         = pos.x;
    anchor.y         = pos.y;
    anchor.inHandle  = { x: anchor.inHandle.x  + dx, y: anchor.inHandle.y  + dy };
    anchor.outHandle = { x: anchor.outHandle.x + dx, y: anchor.outHandle.y + dy };
    anchors[anchorIdx] = anchor;

    onShapeUpdate({ ...shape, anchors });
  };

  // ── Out-handle drag ────────────────────────────────────────────────────────
  const handleOutHandleDrag = (
    e: Konva.KonvaEventObject<DragEvent>,
    anchorIdx: number
  ) => {
    const pos     = nodePos(e);
    const anchors = shape.anchors.slice();
    const anchor  = { ...anchors[anchorIdx] };
    anchor.outHandle = { x: pos.x, y: pos.y };
    if (anchor.smooth) anchor.inHandle = mirrorHandle(anchor, anchor.outHandle);

    anchors[anchorIdx] = anchor;
    onShapeUpdate({ ...shape, anchors });
  };

  // ── In-handle drag ─────────────────────────────────────────────────────────
  const handleInHandleDrag = (
    e: Konva.KonvaEventObject<DragEvent>,
    anchorIdx: number
  ) => {
    const pos     = nodePos(e);
    const anchors = shape.anchors.slice();
    const anchor  = { ...anchors[anchorIdx] };
    anchor.inHandle = { x: pos.x, y: pos.y };
    if (anchor.smooth) anchor.outHandle = mirrorHandle(anchor, anchor.inHandle);

    anchors[anchorIdx] = anchor;
    onShapeUpdate({ ...shape, anchors });
  };

  // ── Whole-shape drag ──────────────────────────────────────────────────────
  // The Group is draggable. Konva moves the Group's (x,y) in canvas space
  // (it already accounts for stage scale/pan). On dragEnd we bake the offset
  // into every anchor coord and reset the Group back to (0,0) so anchor
  // positions stay authoritative and don't double-count the group offset.
  const handleShapeDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    // Live visual update while dragging — just re-emit so the curve redraws
    // at the Group's current translated position. No anchor mutation yet.
    onShapeUpdate({ ...shape });
  };

  const handleShapeDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    const group = e.target;
    // group.x() / group.y() are in the Layer's coordinate space which equals
    // canvas space (the Layer has no transform). This is the actual pixel delta.
    const dx = group.x();
    const dy = group.y();

    if (dx === 0 && dy === 0) return;

    const anchors = shape.anchors.map(a => ({
      ...a,
      x:         a.x         + dx,
      y:         a.y         + dy,
      inHandle:  { x: a.inHandle.x  + dx, y: a.inHandle.y  + dy },
      outHandle: { x: a.outHandle.x + dx, y: a.outHandle.y + dy },
    }));

    // Reset group position BEFORE calling onShapeDragEnd so the next render
    // paints anchors at their new absolute positions with group at (0,0).
    group.position({ x: 0, y: 0 });
    onShapeDragEnd({ ...shape, anchors });
  };

  return (
    <Group
      id={shape.id}
      draggable={tool === 'select'}
      onDragMove={handleShapeDragMove}
      onDragEnd={handleShapeDragEnd}
    >
      {/* Invisible bounding-box rect — sole purpose is to be a large, easy-to-hit
          drag surface for the whole shape, exactly like how Figma/Illustrator let
          you grab anywhere near a path. It sits below everything else visually.  */}
      {tool === 'select' && bounds.w > 0 && (
        <Rect
          x={bounds.x - 8}
          y={bounds.y - 8}
          width={bounds.w + 16}
          height={bounds.h + 16}
          fill="transparent"
          stroke="transparent"
          strokeWidth={0}
          onClick={() => onSelect(shape.id)}
          onTap={() => onSelect(shape.id)}
        />
      )}

      {/* Main cubic Bézier path (real ctx.bezierCurveTo rendering) */}
      <KonvaShape
        sceneFunc={sceneFunc}
        hitFunc={hitFunc}
        onClick={() => { if (tool === 'select') onSelect(shape.id); }}
        onTap={() =>    { if (tool === 'select') onSelect(shape.id); }}
      />

      {/* Length label on hover / selection */}
      {(isSelected || isHovered) && bounds.w > 0 && (
        <KonvaText
          x={bounds.x + bounds.w / 2 - 40 / stageScale}
          width={80 / stageScale}
          y={bounds.y - 20 / stageScale}
          text={formatMeasurement(
            shape.anchors.reduce((acc, a, i, arr) => {
              if (i === 0) return acc;
              const prev = arr[i - 1];
              const dx   = a.x - prev.x;
              const dy   = a.y - prev.y;
              return acc + Math.sqrt(dx * dx + dy * dy);
            }, 0)
          )}
          align="center"
          fontSize={Math.max(10, 12 / stageScale)}
          listening={false}
          fill="#444"
        />
      )}

      {/* Edit handles — anchor circles + handle circles + guide lines */}
      {isSelected && shape.anchors.map((anchor, i) => {
        const hasOut = Math.abs(anchor.outHandle.x - anchor.x) > 0.5
                    || Math.abs(anchor.outHandle.y - anchor.y) > 0.5;
        const hasIn  = Math.abs(anchor.inHandle.x  - anchor.x) > 0.5
                    || Math.abs(anchor.inHandle.y  - anchor.y) > 0.5;

        return (
          <React.Fragment key={anchor.id}>
            {hasIn && (
              <Line
                points={[anchor.x, anchor.y, anchor.inHandle.x, anchor.inHandle.y]}
                stroke={GUIDE_STROKE}
                strokeWidth={1 / stageScale}
                dash={[4 / stageScale, 4 / stageScale]}
                listening={false}
              />
            )}
            {hasOut && (
              <Line
                points={[anchor.x, anchor.y, anchor.outHandle.x, anchor.outHandle.y]}
                stroke={GUIDE_STROKE}
                strokeWidth={1 / stageScale}
                dash={[4 / stageScale, 4 / stageScale]}
                listening={false}
              />
            )}

            {hasIn && (
              <Circle
                x={anchor.inHandle.x}
                y={anchor.inHandle.y}
                radius={hr}
                fill={HANDLE_FILL}
                stroke={HANDLE_STROKE}
                strokeWidth={1 / stageScale}
                draggable
                onDragStart={(e) => e.cancelBubble = true}
                onDragMove={(e) => { e.cancelBubble = true; handleInHandleDrag(e, i); }}
                onDragEnd={(e) => { e.cancelBubble = true; }}
              />
            )}

            {hasOut && (
              <Circle
                x={anchor.outHandle.x}
                y={anchor.outHandle.y}
                radius={hr}
                fill={HANDLE_FILL}
                stroke={HANDLE_STROKE}
                strokeWidth={1 / stageScale}
                draggable
                onDragStart={(e) => e.cancelBubble = true}
                onDragMove={(e) => { e.cancelBubble = true; handleOutHandleDrag(e, i); }}
                onDragEnd={(e) => { e.cancelBubble = true; }}
              />
            )}

            <Circle
              x={anchor.x}
              y={anchor.y}
              radius={r}
              fill={ANCHOR_FILL}
              stroke={ANCHOR_STROKE}
              strokeWidth={1.5 / stageScale}
              draggable
              onDragStart={(e) => e.cancelBubble = true}
              onDragMove={(e) => { e.cancelBubble = true; handleAnchorDrag(e, i); }}
              onDragEnd={(e) => { e.cancelBubble = true; }}
            />
          </React.Fragment>
        );
      })}
    </Group>
  );
};

// ── Main exported layer ───────────────────────────────────────────────────────
const BezierPenLayer: React.FC<BezierPenLayerProps> = ({
  shapes,
  selectedId,
  stageScale,
  tool,
  onSelect,
  onShapeUpdate,
  onShapeDragEnd,
  formatMeasurement,
}) => {
  const [hoveredId, setHoveredId] = React.useState<string | null>(null);

  return (
    <Layer>
      {shapes.map(shape => (
        <Group
          key={shape.id}
          onMouseEnter={() => setHoveredId(shape.id)}
          onMouseLeave={() =>
            setHoveredId(prev => (prev === shape.id ? null : prev))
          }
        >
          <BezierShapeNode
            shape={shape}
            isSelected={selectedId === shape.id}
            isHovered={hoveredId === shape.id}
            stageScale={stageScale}
            tool={tool}
            onSelect={onSelect}
            onShapeUpdate={onShapeUpdate}
            onShapeDragEnd={onShapeDragEnd}
            formatMeasurement={formatMeasurement}
          />
        </Group>
      ))}
    </Layer>
  );
};

export default BezierPenLayer;